import { CustomTooltip } from '@/components/custom-tooltip';
import { Switch } from '@mui/material';
import styles from 'common/_variables.module.scss';
import type React from 'react';
import styled from 'styled-components';

const GreenSwitch = styled(Switch)(() => ({
	'& .MuiSwitch-switchBase': {
		color: styles.colorGreen,
	},
	'& .MuiSwitch-switchBase + .MuiSwitch-track': {
		backgroundColor: styles.colorGreen,
	},
}));

export interface UniversalSettingsToggleProps {
	/**
	 * Whether universal settings are currently enabled
	 */
	isUniversal: boolean;

	/**
	 * Callback when the toggle state changes
	 */
	onToggle: (isUniversal: boolean) => void;

	/**
	 * Whether the toggle is disabled (e.g., during loading)
	 */
	disabled?: boolean;

	/**
	 * Optional custom className for styling
	 */
	className?: string;
}

/**
 * UniversalSettingsToggle - Controls whether column settings apply universally
 * across all properties or are specific to the current property.
 *
 * When enabled (universal):
 * - Settings are saved with scope='universal' and property_id=NULL
 * - Changes apply to all properties the user manages
 *
 * When disabled (property-specific):
 * - Settings are saved with scope='property' and current property_id
 * - Changes only affect the current property
 */
export const UniversalSettingsToggle: React.FC<
	UniversalSettingsToggleProps
> = ({ isUniversal, onToggle, disabled = false, className = '' }) => {
	const handleToggle = () => {
		if (!disabled) {
			onToggle(!isUniversal);
		}
	};

	const tooltipContent = (
		<div className='tw-max-w-xs'>
			<p className='tw-font-semibold'>
				{isUniversal
					? 'Universal Settings (Enabled)'
					: 'Property-Specific Settings'}
			</p>
			<p className='tw-text-xs tw-mb-1'>
				{isUniversal
					? 'Column layout applies to all properties you manage.'
					: 'Column layout is specific to this property only.'}
			</p>
		</div>
	);

	return (
		<div className={`tw-flex tw-items-center ${className}`}>
			<CustomTooltip
				content={tooltipContent}
				arrow
				borderColor='tw-border-primary'
			>
				<GreenSwitch
					checked={isUniversal}
					onChange={handleToggle}
					disabled={disabled}
					size='small'
					inputProps={{
						'aria-label': isUniversal
							? 'Universal settings enabled'
							: 'Property-specific settings',
					}}
				/>
			</CustomTooltip>
		</div>
	);
};
